import React, {
  useEffect, useMemo, useState, useRef,
} from 'react';
import { format } from 'date-fns';
import { useApiClient, useFetch } from '@marageti/z4-lib';
import {
  PartnerSalesRecordingTotals,
  SalesRecordingAggregateStatus,
  SalesRecordingExportRequest,
  SalesRecordingStatus,
  SalesRecordingTotals,
} from '@marageti/z4-sdk/lib/travel/SalesRecording';
import { useLocation, useParams } from 'react-router-dom';
import { FilterOp, SearchRequest } from '@marageti/z4-sdk/lib/entity';
import { Doc } from '@marageti/z4-sdk/lib/documents';
import { enqueueSnackbar } from 'notistack';
import { ApiError } from '@marageti/z4-sdk/lib/tracking';
import PartnerInvoiceView from './partner-invoice-view';
import useDocumentDownload from '../../../hooks/use-document-download';
import useSalesRecordingsWithTripMatches from '../../../hooks/use-sales-recordings-with-trip-matches';

const today = new Date();
const REFRESH_LIMIT = 10;

const PartnerInvoiceContainer = () => {
  const [onDownloadDocument, docError] = useDocumentDownload();
  const apiClient = useApiClient();
  const { partnerId } = useParams();
  const location = useLocation();

  const selectedMonth = location.state?.selectedMonth ?? today;
  const formattedDate = format(selectedMonth || today, 'yyyy-MM');

  const {
    data: salesRecordingsData,
    isLoading: salesLoading,
    mutate: salesMutate,
  } = useFetch<PartnerSalesRecordingTotals>(apiClient.partnersClient.getSalesRecordingTotalsForMonth, [{ params: { month: formattedDate } }], true);
  const aggregateStatus = salesRecordingsData?.partners.find((p: SalesRecordingTotals) => p.partnerId === partnerId)?.aggregateStatus;
  const [disableExportWhileProcessing, setDisableExportWhileProcessing] = useState(false);

  const searchRequest: SearchRequest = {
    filterBy: [{
      field: 'partnerId',
      op: FilterOp.FilterOpTerm,
      value: [partnerId!],
    },
    {
      field: 'invoiceDate',
      op: FilterOp.FilterOpTerm,
      value: [`${formattedDate}`],
    },
    ],
    pagination: { limit: 10000, page: 1 },
    sortBy: [{ field: 'createdAt', order: 'asc' }],
  };

  // Use unified hook to get sales recordings with trip matches
  const {
    data: salesRecordingsWithTripMatch,
    isLoading,
    mutate,
    error,
  } = useSalesRecordingsWithTripMatches(
    searchRequest,
    !!partnerId,
  );

  const totalSales = useMemo(() => {
    if (salesRecordingsWithTripMatch && salesRecordingsWithTripMatch.length > 0) {
      const ts = salesRecordingsWithTripMatch.reduce((total, hit) => Math.round(hit.price.amountUSD * 100) / 100 + total, 0);
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(ts);
    }
    return undefined;
  }, [salesRecordingsWithTripMatch]);

  const totalCommission = useMemo(() => {
    if (salesRecordingsWithTripMatch && salesRecordingsWithTripMatch.length > 0) {
      const tc = salesRecordingsWithTripMatch.reduce((total, hit) => Math.round(hit.commission.amountUSD * 100) / 100 + total, 0);
      return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(tc);
    }
    return undefined;
  }, [salesRecordingsWithTripMatch]);

  useEffect(() => {
    if (aggregateStatus === SalesRecordingAggregateStatus.SalesRecordingAggregateStatusError) {
      setDisableExportWhileProcessing(false);
    }
    // if the aggregateStatus is processing call mutate every 1.5 seconds until it is not longer processing or has been called 10 times
    if (aggregateStatus === SalesRecordingAggregateStatus.SalesRecordingAggregateStatusProcessing && disableExportWhileProcessing) {
      let count = 0;
      const interval = setInterval(() => {
        if (count < REFRESH_LIMIT) {
          count += 1;
          mutate();
          salesMutate();
        } else {
          setDisableExportWhileProcessing(false);
          clearInterval(interval);
        }
      }, 1500);
      return () => clearInterval(interval);
    }
    return undefined;
  }, [aggregateStatus, mutate, salesMutate]);

  const onClickDocument = (doc: Doc) => {
    onDownloadDocument(doc);
  };

  const updateStatus = async (salesRecordingId: string, salesRecordingStatus: SalesRecordingStatus) => {
    const salesRecordingStatusUpdate = { id: salesRecordingId, status: salesRecordingStatus };
    await apiClient.salesRecordingsClient.putSalesRecordingStatus(salesRecordingId, salesRecordingStatusUpdate);
    mutate();
    salesMutate();
  };

  const handleStatusUpdate = async (salesRecordingId: string, salesRecordingStatus: SalesRecordingStatus) => {
    if (salesRecordingStatus === SalesRecordingStatus.InvoicedStatus) {
      enqueueSnackbar('Sales Recording is already invoiced to QuickBooks and cannot be modified.', { variant: 'default' });
      return;
    }
    try {
      const newStatus = salesRecordingStatus === SalesRecordingStatus.SoldStatus ? SalesRecordingStatus.VerifiedStatus : SalesRecordingStatus.SoldStatus;
      await updateStatus(salesRecordingId, newStatus);
      const message = newStatus === SalesRecordingStatus.VerifiedStatus ? 'Sales Recording set to Approved' : 'Sales Recording set to Unapproved';
      enqueueSnackbar(message, { variant: 'success' });
    } catch {
      enqueueSnackbar('There was an error updating the Sales Recording Status', { variant: 'default' });
    }
  };

  const [isLoadingExport, setIsLoadingExport] = useState(false);
  const isProcessingRef = useRef(false); // To prevent multiple simultaneous export requests

  const handleExport = async () => {
    if (isProcessingRef.current || isLoadingExport || disableExportWhileProcessing) return;
    isProcessingRef.current = true;
    setIsLoadingExport(true);
    setDisableExportWhileProcessing(true);

    try {
      const exportRequest: SalesRecordingExportRequest = {
        partnerId: partnerId!,
        invoiceDate: formattedDate,
      };
      await apiClient.salesRecordingsClient.postSalesRecordingsExport(exportRequest);
      mutate();
      salesMutate();
    } catch {
      enqueueSnackbar('There was an error exporting the Sales Recordings', { variant: 'default' });
    } finally {
      setIsLoadingExport(false);
      isProcessingRef.current = false;
    }
  };

  return (
    <PartnerInvoiceView
      aggregateStatus={aggregateStatus}
      disableExportWhileProcessing={disableExportWhileProcessing}
      docError={docError}
      salesRecordingsError={error as ApiError | undefined}
      isLoadingExport={isLoadingExport}
      loading={isLoading || salesLoading}
      onClickDocument={onClickDocument}
      onClickExport={handleExport}
      salesRecordings={salesRecordingsWithTripMatch}
      selectedMonth={selectedMonth}
      totalCommission={totalCommission}
      totalSales={totalSales}
      updateStatus={handleStatusUpdate}
    />
  );
};

export default PartnerInvoiceContainer;
